import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CalendarClock } from 'lucide-react';

/**
 * The virgin EOD scheduling conflict surface.
 *
 * ============================================================================
 * WHY THIS IS NOT EODRolloverModal
 * ============================================================================
 * A never-started ticket that can no longer finish today has a SCHEDULING
 * problem. It has no work to continue, no Part A actuals to preserve, and no
 * remaining minutes to estimate — so every control the rollover modal offers
 * is either meaningless or actively wrong here:
 *
 *   · "Convert to Rollover"        → would fabricate a continuation of work
 *                                     that never happened, and write rollover
 *                                     lineage onto a virgin ticket.
 *   · "Remaining minutes"          → remaining out of what? Nothing has run.
 *   · "Resume Anyway"              → there is no paused timer to resume.
 *
 * This component therefore does not contain them. The guarantee that a virgin
 * conflict can never expose "Convert to Rollover" is structural: the action
 * does not exist in this tree, so no conditional can be inverted to reach it.
 *
 * ============================================================================
 * WHAT IT DOES OFFER
 * ============================================================================
 * The existing scheduling infrastructure, unchanged: `onReschedule` is the
 * container's OptiPlace path — the same one the rollover modal's "Reassign"
 * button already used — relabelled to say what it actually does for a ticket
 * that has not started.
 *
 * Dismissing changes nothing. The ticket stays virgin with its honest Start /
 * OptiFinish projection, the card keeps showing the scheduling-risk indicator,
 * and General AutoBump keeps advancing Start Time. Dismissal is "not now", not
 * "resolved" — and deliberately writes no state, because a persisted
 * "waiting for decision" status is not part of this repair.
 */
export interface VirginEodSchedulingModalProps {
  open: boolean;
  onClose: () => void;
  customerName: string;
  techName: string;
  /** The technician's configured shift end. Display only. */
  techEOD: string;
  /** How far the projected finish lands past that shift end. */
  minutesPastShiftEnd: number;
  /** Opens OptiPlace for this ticket. Supplied by EODRolloverContainer. */
  onReschedule: () => void;
}

export function VirginEodSchedulingModal({
  open,
  onClose,
  customerName,
  techName,
  techEOD,
  minutesPastShiftEnd,
  onReschedule,
}: VirginEodSchedulingModalProps) {
  const [rescheduling, setRescheduling] = useState(false);

  useEffect(() => {
    if (open) setRescheduling(false);
  }, [open]);

  const handleReschedule = () => {
    setRescheduling(true);
    onReschedule();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent data-testid="virgin-eod-scheduling-modal" className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
            <CalendarClock className="h-5 w-5" />
            <DialogTitle>Cannot finish today — scheduling decision required</DialogTitle>
          </div>
          {/* `DialogDescription` renders a <p>, so these are <span
              className="block"> rather than nested <p> elements — the latter is
              invalid nesting and React says so at runtime. */}
          <DialogDescription className="space-y-2 pt-2">
            <span className="block">
              The ticket for <strong>{customerName}</strong> has not started, and
              as planned it would run <strong>{minutesPastShiftEnd} minutes</strong>{' '}
              past <strong>{techName}'s</strong> end of day ({techEOD}).
            </span>
            <span className="block text-sm text-muted-foreground">
              No work has been performed on this ticket, so there is nothing to
              roll over. It stays scheduled and keeps moving forward with the
              clock — you can reschedule or reassign it whenever you are ready.
            </span>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex-col sm:flex-row sm:flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
            data-testid="virgin-eod-dismiss-button"
          >
            Dismiss
          </Button>
          <Button
            variant="default"
            onClick={handleReschedule}
            disabled={rescheduling}
            className="w-full sm:w-auto"
            data-testid="virgin-eod-reschedule-button"
          >
            {rescheduling ? 'Opening…' : 'Reschedule / Reassign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
